import { Pencil, Trash2 } from 'lucide-react'
import { OwnedChip, isConnectionOwned } from '@/components/OwnedChip'
import { EnrolmentBadge, EnrolmentGrade, type Enrolment } from './enrolment'
import type { ListPageColumn } from '@/components/templates/list-page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { displayDate } from '@/lib/dates'

export interface RosterState {
  search: string
  status: string
  owner: string
  sort: string
  direction: string
}
export const initialRosterState: RosterState = {
  search: '',
  status: '',
  owner: '',
  sort: 'enrolled_on',
  direction: 'desc',
}

export function rosterColumns(
  state: RosterState,
  toggleSort: (sort: string) => void,
  unit: string,
  actions: {
    viewOnly: boolean
    onEdit: (row: Enrolment) => void
    onDelete: (row: Enrolment) => void
    courseStatuses?: Map<number, string>
  },
): ListPageColumn<Enrolment>[] {
  const sortable = (column: string) => ({
    active: state.sort === column,
    direction: state.direction === 'asc' ? ('ascending' as const) : ('descending' as const),
    onToggle: () => toggleSort(column),
  })
  const dateColumn = (id: 'enrolled_on' | 'completed_on', header: string) => ({
    id,
    header,
    sort: sortable(id),
    className: 'text-muted-foreground',
    cell: (row: Enrolment) => (row[id] ? displayDate(row[id]) : null),
  })
  return [
    {
      id: actions.courseStatuses ? 'course' : 'person',
      header: actions.courseStatuses ? 'Course' : 'Person',
      sort: sortable(actions.courseStatuses ? 'course' : 'person'),
      cell: (row) =>
        actions.courseStatuses ? (
          <CourseCell row={row} statuses={actions.courseStatuses} />
        ) : (
          <div id={`enrolment-${row.id}`} tabIndex={-1}>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={`/contacts/persons/${row.person_id}`}
                className="font-medium text-foreground hover:underline"
              >
                {row.person_name}
              </a>
              {row.person_archived && <Badge variant="neutral">Archived</Badge>}
              <OwnedChip owner={row.owner} />
            </div>
            <span className="block text-caption text-muted-foreground">{row.person_email}</span>
          </div>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      sort: sortable('status'),
      cell: (row) => <EnrolmentBadge status={row.status} />,
    },
    dateColumn('enrolled_on', 'Enrolled on'),
    dateColumn('completed_on', 'Completed on'),
    { id: 'grade', header: 'Grade', cell: (row) => <EnrolmentGrade enrolment={row} /> },
    {
      id: 'credits',
      header: unit,
      headerClassName: 'text-right',
      className: 'text-right tabular-nums',
      cell: (row) => row.credits_earned,
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      headerClassName: 'relative w-20',
      className: 'w-20 py-0 text-right',
      cell: (row) =>
        actions.viewOnly || isConnectionOwned(row.owner) ? null : (
          <EnrolmentActions row={row} {...actions} />
        ),
    },
  ]
}

function EnrolmentActions({
  row,
  onEdit,
  onDelete,
  courseStatuses,
}: {
  row: Enrolment
  onEdit: (row: Enrolment) => void
  onDelete: (row: Enrolment) => void
  courseStatuses?: Map<number, string>
}) {
  const label = courseStatuses ? `in ${row.course_title}` : `for ${row.person_name}`
  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Edit enrolment ${label}`}
        title={`Edit enrolment ${label}`}
        onClick={() => onEdit(row)}
      >
        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="text-muted-foreground hover:text-destructive"
        aria-label={`Delete enrolment ${label}`}
        title={`Delete enrolment ${label}`}
        onClick={() => onDelete(row)}
      >
        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
      </Button>
    </div>
  )
}

function CourseCell({ row, statuses }: { row: Enrolment; statuses: Map<number, string> }) {
  return (
    <div id={`enrolment-${row.id}`} tabIndex={-1} className="flex flex-wrap items-center gap-2">
      <a
        href={`/courses/${row.course_id}`}
        className="whitespace-nowrap font-medium text-foreground hover:underline"
      >
        {row.course_title}
      </a>
      {statuses.get(row.course_id) === 'archived' && <Badge variant="neutral">Archived</Badge>}
      <OwnedChip owner={row.owner} />
    </div>
  )
}
